import { BRAND_MODES, FOOD_AMIGOS_BRAND, NEUTRAL_BRAND } from './brands';

// A language list is trusted only when it is an array. A company resource broadcast from an
// unloaded Laravel relation ships `{}` for its `whenLoaded` fields, and the portal persisted such
// a hole in `company.locales` for years — the brand is the first code to iterate it.
function languageList(value) {
  return Array.isArray(value) ? value : [];
}

// The partner's languages, primary first. Both payloads now carry `language` +
// `languages` (ServiceProvider::getLanguages() — the partner's own default plus its
// region's locales), so the session brand is gated on what the partner offers rather
// than on the outlet's geography. `locales` stays only as the fallback for a cached
// session brand written before that field existed. Empty when neither is known
// (callers fall back to the bundled locales).
function brandLanguagesFromSource(source, locales = []) {
  const languages = languageList(source.languages ?? locales);
  const language = typeof source.language === 'string' ? source.language : (languages[0] ?? null);

  return {
    language,
    languages: [...new Set([language, ...languages].filter(Boolean))],
  };
}

function partnerBrand(mode, source, locales) {
  return {
    mode,
    name: source.name ?? NEUTRAL_BRAND.name,
    logoUrl: source.logo_url ?? null,
    circleLogoUrl: source.circle_logo_url ?? null,
    primaryColor: source.primary_color ?? NEUTRAL_BRAND.primaryColor,
    ...brandLanguagesFromSource(source, locales),
  };
}

// Session brand: the current service provider from /staff/profile, whose payload carries
// its own `languages`; the company's region `locales` are only a fallback for a brand
// cached before that field shipped. The master provider IS the white-labeling platform,
// so it always gets the FoodAmigos identity — white-label branding is only for partners.
// A provider without an `id` is not a session either: the `orders.paused` broadcast used to
// carry `service_provider: {}`, which the pause handler merged into the persisted company.
export function brandFromServiceProvider(serviceProvider, locales = []) {
  if (!serviceProvider?.id) {
    return null;
  }

  if (serviceProvider.is_master) {
    return FOOD_AMIGOS_BRAND;
  }

  return partnerBrand(BRAND_MODES.PARTNER, serviceProvider, locales);
}

// Logged-out brand: the /api/brand-config?partner= payload.
export function brandFromConfigResponse(data) {
  if (!data || data.is_neutral) {
    return NEUTRAL_BRAND;
  }

  return partnerBrand(BRAND_MODES.PARTNER, data);
}
